import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { SignalRService, BulkUploadTaskMatchResultPayload, DocOcrAdrStatusUpdatePayload, DocOcrAdrCompletePayload } from '../../services/signalr.service';
import { BasePortalService } from '../../services/base-portal.service';
import { NotificationService } from '../../services/notification.service';
import { DirectUploadService, DirectUploadProgress } from '../../services/direct-upload.service';
import { ApplicationTaskModel, BorrowerTaskType } from '../../models/borrower-portal/application-task-model';
import { ApplicationTasksModel } from '../../models/borrower-portal/application-tasks-model';

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

export type BulkUploadFileStatus =
  | 'uploading'
  | 'converting'
  | 'identifying'
  | 'extracting'
  | 'matching'
  | 'matched'
  | 'needs-selection'
  | 'split'
  | 'confirmed'
  | 'error';

export interface ProcessingStage {
  key: string;
  label: string;
  complete: boolean;
  active: boolean;
}

/** Maximum file size in bytes (50 MB) */
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

/** Maximum number of files per batch */
const MAX_FILE_COUNT = 25;

/** Polling interval in ms when SignalR is disconnected */
const POLL_INTERVAL_MS = 5000;

/** Delay (ms) before an auto-matched file is confirmed and removed */
const AUTO_CONFIRM_DELAY_MS = 3000;

export interface BulkUploadFileItem {
  /** Client-side id */
  id: number;
  file: File;
  status: BulkUploadFileStatus;

  /** Returned from the bulk-upload API */
  docFileId?: number;
  fileGuid?: string;

  /** Thumbnail data-URL (images) or canvas snapshot (PDFs) */
  thumbnailUrl?: string;

  /** From ADR */
  extractedDocumentTypeName?: string;

  /** From AI matching */
  matchedTaskId?: number | null;
  matchedTaskDescription?: string | null;
  confidence?: number;
  requiresManualSelection: boolean;

  /** Manual task selection by borrower — use -1 for "No matching task" */
  selectedTaskId?: number;

  /** Borrower note explaining why they uploaded a document (especially when no task matches) */
  borrowerNote?: string;

  /** Processing pipeline visualisation */
  processingStages: ProcessingStage[];

  errorMessage?: string;

  /** True while the slide-out animation is playing */
  isSliding?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'bulk-upload',
  templateUrl: './bulk-upload.component.html',
  styleUrls: ['./bulk-upload.component.scss'],
  standalone: false
})
export class BulkUploadComponent implements OnInit, OnDestroy {

  @Input() applicationId: number;
  @Input() isAgent: boolean = false;

  /** Emitted after the borrower confirms all assignments so the parent can refresh tasks. */
  @Output() confirmed = new EventEmitter<void>();

  fileItems: BulkUploadFileItem[] = [];
  availableTasks: ApplicationTaskModel[] = [];
  validationErrors: string[] = [];

  isDragOver = false;
  isUploading = false;
  isConfirming = false;
  showProcessingPanel = false;
  isFlippingBack = false;

  private _nextId = 1;
  private _subscriptions: Subscription[] = [];
  private _pollingSubscription?: Subscription;
  private _pollingActive = false;
  private _pollingInFlight = new Set<number>();
  private _destroyed = false;
  private _autoResetTimer?: ReturnType<typeof setTimeout>;
  /** Per-file timers for auto-confirming matched files */
  private _autoConfirmTimers = new Map<number, ReturnType<typeof setTimeout>>();
  /** Tracks files currently being auto-confirmed to prevent duplicates */
  private _autoConfirmInFlight = new Set<number>();

  constructor(
    private readonly _http: HttpClient,
    private readonly _environment: EnvironmentService,
    private readonly _signalRService: SignalRService,
    private readonly _portalService: BasePortalService,
    private readonly _notifyService: NotificationService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone,
    private readonly _directUploadService: DirectUploadService
  ) {}

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this._subscribeToSignalR();
    this._loadAvailableTasks();
    this._loadPendingBulkUploads();
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this._subscriptions.forEach(s => s.unsubscribe());
    this._stopPolling();
    this._revokeAllThumbnails();
    if (this._autoResetTimer) {
      clearTimeout(this._autoResetTimer);
    }
    this._autoConfirmTimers.forEach(t => clearTimeout(t));
    this._autoConfirmTimers.clear();
  }

  // ---------------------------------------------------------------------------
  // Drag & drop handlers
  // ---------------------------------------------------------------------------

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this._addFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this._addFiles(Array.from(input.files));
      input.value = '';
    }
  }

  // ---------------------------------------------------------------------------
  // Public helpers
  // ---------------------------------------------------------------------------

  get allProcessed(): boolean {
    return this.fileItems.length > 0 &&
      this.fileItems.every(f =>
        f.status === 'matched' ||
        f.status === 'needs-selection' ||
        f.status === 'split' ||
        f.status === 'confirmed' ||
        f.status === 'error'
      );
  }

  get canConfirm(): boolean {
    return this.allProcessed &&
      this.fileItems.some(f => f.status === 'matched' || f.status === 'needs-selection') &&
      this.fileItems
        .filter(f => f.status === 'needs-selection')
        .every(f => f.selectedTaskId !== undefined && f.selectedTaskId !== null);
  }

  get hasConfirmedFiles(): boolean {
    return !this.isConfirming && this.allProcessed && this.fileItems.some(f => f.status === 'confirmed');
  }

  get hasUnconfirmedFiles(): boolean {
    return this.allProcessed && !this.fileItems.every(f => f.status === 'confirmed' || f.status === 'error' || f.status === 'split');
  }

  getStageIcon(stage: ProcessingStage): string {
    if (stage.complete) return 'fa-check-circle text-success';
    if (stage.active) return 'fa-spinner fa-spin text-primary';
    return 'fa-circle text-muted';
  }

  onTaskSelected(fileItem: BulkUploadFileItem, taskId: number): void {
    fileItem.selectedTaskId = taskId;
    // Clear borrower note when switching away from "No matching task"
    if (taskId !== -1) {
      fileItem.borrowerNote = undefined;
    }
    this._cdr.detectChanges();
  }

  onBorrowerNoteChanged(fileItem: BulkUploadFileItem, note: string): void {
    fileItem.borrowerNote = note;
  }

  /** Confirm a single file row — called from the per-row confirm button */
  confirmSingleRow(item: BulkUploadFileItem): void {
    if (!item.docFileId) {
      return;
    }

    // Cancel any pending auto-confirm timer for this file
    const pendingTimer = this._autoConfirmTimers.get(item.id);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      this._autoConfirmTimers.delete(item.id);
    }

    // If auto-confirm is already in flight, don't double-submit
    if (this._autoConfirmInFlight.has(item.id)) {
      return;
    }

    const taskId = item.status === 'matched'
      ? item.matchedTaskId
      : (item.selectedTaskId === -1 ? null : item.selectedTaskId);

    this._autoConfirmInFlight.add(item.id);

    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
    const url = `${docApiBase}/api/portal/bulk-upload/confirm/${this.applicationId}`;

    this._http.post<{ confirmResults: any[] }>(url, {
      applicationId: this.applicationId,
      assignments: [{
        docFileId: item.docFileId,
        taskId: taskId ?? null,
        borrowerNote: item.borrowerNote || null
      }]
    }).subscribe({
      next: () => {
        this._autoConfirmInFlight.delete(item.id);
        if (this._destroyed) {
          return;
        }

        item.status = 'confirmed';
        item.isSliding = true;
        this._cdr.detectChanges();

        // After the slide-out animation finishes, remove the item and refresh tasks
        setTimeout(() => {
          if (this._destroyed) {
            return;
          }

          if (item.thumbnailUrl && item.thumbnailUrl.startsWith('blob:')) {
            URL.revokeObjectURL(item.thumbnailUrl);
          }
          this.fileItems = this.fileItems.filter(f => f.id !== item.id);

          if (this.fileItems.length === 0) {
            this.showProcessingPanel = false;
          }

          this.confirmed.emit();
          this._cdr.detectChanges();
        }, 500);
      },
      error: (err) => {
        this._autoConfirmInFlight.delete(item.id);
        const msg = err?.error?.message || err?.error?.detail || 'Failed to confirm this document.';
        this._notifyService.showError(msg, 'Bulk Upload');
        this._cdr.detectChanges();
      }
    });
  }

  /** Whether a single row can be individually confirmed */
  canConfirmRow(item: BulkUploadFileItem): boolean {
    if (this._autoConfirmInFlight.has(item.id)) {
      return false;
    }
    if (item.status === 'matched') {
      return true;
    }
    if (item.status === 'needs-selection') {
      return item.selectedTaskId !== undefined && item.selectedTaskId !== null;
    }
    return false;
  }

  /** Retry a single failed file by re-uploading it */
  retryFile(item: BulkUploadFileItem): void {
    item.status = 'uploading';
    item.errorMessage = undefined;
    item.processingStages = this._createInitialStages();
    item.docFileId = undefined;
    item.fileGuid = undefined;
    item.matchedTaskId = undefined;
    item.matchedTaskDescription = undefined;
    item.confidence = undefined;
    item.requiresManualSelection = false;
    item.selectedTaskId = undefined;
    this._uploadFiles([item]);
  }

  /** Remove a file from the list and soft-delete from backend if already uploaded */
  removeFile(item: BulkUploadFileItem): void {
    if (item.thumbnailUrl && item.thumbnailUrl.startsWith('blob:')) {
      URL.revokeObjectURL(item.thumbnailUrl);
    }

    // If the file was already uploaded, soft-delete it on the server
    if (item.docFileId) {
      const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
      const url = `${docApiBase}/api/portal/bulk-upload/${this.applicationId}/${item.docFileId}`;
      this._http.delete(url).subscribe({
        error: (err) => {
          console.warn('Failed to delete file on server:', err);
        }
      });
    }

    this.fileItems = this.fileItems.filter(f => f.id !== item.id);
    if (this.fileItems.length === 0) {
      this.showProcessingPanel = false;
    }
    this._cdr.detectChanges();
  }

  /** Dismiss validation error messages */
  dismissValidationErrors(): void {
    this.validationErrors = [];
  }

  // ---------------------------------------------------------------------------
  // Confirm & submit
  // ---------------------------------------------------------------------------

  confirmAll(): void {
    if (!this.canConfirm || this.isConfirming) {
      return;
    }

    this.isConfirming = true;

    const assignments = this.fileItems
      .filter(f => f.status === 'matched' || f.status === 'needs-selection')
      .filter(f => !!f.docFileId)
      .map(f => {
        const isNoTask = f.selectedTaskId === -1;
        const taskId = f.status === 'matched'
          ? f.matchedTaskId
          : (isNoTask ? null : f.selectedTaskId);
        return {
          docFileId: f.docFileId,
          taskId: taskId ?? null,
          borrowerNote: f.borrowerNote || null
        };
      });

    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
    const url = `${docApiBase}/api/portal/bulk-upload/confirm/${this.applicationId}`;

    this._http.post<{ confirmResults: any[] }>(url, {
      applicationId: this.applicationId,
      assignments
    }).subscribe({
      next: () => {
        this.fileItems.forEach(f => {
          if (f.status === 'matched' || f.status === 'needs-selection') {
            f.status = 'confirmed';
          }
        });
        this.isConfirming = false;
        this._notifyService.showSuccess('Documents uploaded and attached successfully!', 'Bulk Upload');
        this.confirmed.emit();
        this._cdr.detectChanges();
        this._scheduleAutoReset();
      },
      error: (err) => {
        this.isConfirming = false;
        const msg = err?.error?.message || err?.error?.detail || 'Failed to confirm document assignments.';
        this._notifyService.showError(msg, 'Bulk Upload');
        this._cdr.detectChanges();
      }
    });
  }

  resetUpload(): void {
    if (this._autoResetTimer) {
      clearTimeout(this._autoResetTimer);
      this._autoResetTimer = undefined;
    }
    this.isFlippingBack = false;
    this._revokeAllThumbnails();
    this._stopPolling();
    this.fileItems = [];
    this.showProcessingPanel = false;
    this.isUploading = false;
    this.isConfirming = false;
    this.validationErrors = [];
    this._loadAvailableTasks();
  }

  // ---------------------------------------------------------------------------
  // Private — auto-reset after confirmation
  // ---------------------------------------------------------------------------

  /** After all files are confirmed, wait ~4s then flip back to the drop zone */
  private _scheduleAutoReset(): void {
    // Only auto-reset if every file is confirmed (no errors or pending)
    const allConfirmed = this.fileItems.length > 0
      && this.fileItems.every(f => f.status === 'confirmed');
    if (!allConfirmed) {
      return;
    }

    if (this._autoResetTimer) {
      clearTimeout(this._autoResetTimer);
    }

    this._autoResetTimer = setTimeout(() => {
      if (this._destroyed) {
        return;
      }
      this.isFlippingBack = true;
      this._cdr.detectChanges();

      // Wait for the flip-out animation to finish (~600ms), then reset
      setTimeout(() => {
        if (this._destroyed) {
          return;
        }
        this.resetUpload();
        this._cdr.detectChanges();
      }, 600);
    }, 4000);
  }

  // ---------------------------------------------------------------------------
  // Private — file handling
  // ---------------------------------------------------------------------------

  private _addFiles(files: File[]): void {
    this.validationErrors = [];

    // --- Validation ---
    const remainingSlots = MAX_FILE_COUNT - this.fileItems.length;
    if (remainingSlots <= 0) {
      this.validationErrors.push(`Maximum of ${MAX_FILE_COUNT} files per batch. Remove some files before adding more.`);
      return;
    }

    let filesToAdd = files;
    if (files.length > remainingSlots) {
      this.validationErrors.push(
        `Only ${remainingSlots} more file(s) can be added (limit ${MAX_FILE_COUNT}). The first ${remainingSlots} were added.`
      );
      filesToAdd = files.slice(0, remainingSlots);
    }

    const validFiles: File[] = [];
    for (const file of filesToAdd) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        this.validationErrors.push(`"${file.name}" exceeds the 50 MB size limit and was skipped.`);
        continue;
      }

      const duplicate = this.fileItems.find(
        f => f.file.name === file.name && f.file.size === file.size
      );
      if (duplicate) {
        this.validationErrors.push(`"${file.name}" appears to be a duplicate and was skipped.`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      return;
    }

    const newItems: BulkUploadFileItem[] = validFiles.map(file => ({
      id: this._nextId++,
      file,
      status: 'uploading' as BulkUploadFileStatus,
      requiresManualSelection: false,
      processingStages: this._createInitialStages(),
      thumbnailUrl: this._generateThumbnail(file)
    }));

    this.fileItems.push(...newItems);
    this.showProcessingPanel = true;
    this._uploadFiles(newItems);
  }

  private _createInitialStages(): ProcessingStage[] {
    return [
      { key: 'uploading', label: 'Uploading', complete: false, active: true },
      { key: 'converting', label: 'Converting', complete: false, active: false },
      { key: 'identifying', label: 'Identifying', complete: false, active: false },
      { key: 'extracting', label: 'Extracting', complete: false, active: false },
      { key: 'matching', label: 'Matching', complete: false, active: false }
    ];
  }

  private _generateThumbnail(file: File): string | undefined {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return undefined;
  }

  private _revokeAllThumbnails(): void {
    for (const item of this.fileItems) {
      if (item.thumbnailUrl && item.thumbnailUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.thumbnailUrl);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Private — upload
  // ---------------------------------------------------------------------------

  private _uploadFiles(items: BulkUploadFileItem[]): void {
    this.isUploading = true;

    if (items.length === 0) {
      this.isUploading = false;
      return;
    }

    const totalFileCount = items.length;
    const files = items.map(item => item.file);

    this._directUploadService.uploadBulk(
      this.applicationId,
      files,
      totalFileCount,
      undefined,
      (progress: DirectUploadProgress) => {
        const item = items[progress.fileIndex];
        if (item && !progress.done) {
          // Update progress visually during blob upload
        }
      }
    ).subscribe({
      next: (response) => {
        const { results, batchId } = response;

        results.forEach((result, index) => {
          const item = items[index];
          if (result.success) {
            item.docFileId = result.docFileId;
            item.fileGuid = result.fileGuid;
            this._advanceToStage(item, 'converting');
          } else {
            item.status = 'error';
            item.errorMessage = result.error || 'Upload failed';
            this._clearStages(item);
          }
        });

        this.isUploading = false;
        this._startPollingIfNeeded();
        this._cdr.detectChanges();
      },
      error: (err) => {
        items.forEach(item => {
          item.status = 'error';
          item.errorMessage = err?.error?.message || err?.error?.detail || 'Upload failed';
          this._clearStages(item);
        });

        this.isUploading = false;
        this._startPollingIfNeeded();
        this._cdr.detectChanges();
      }
    });
  }



  // ---------------------------------------------------------------------------
  // Private — SignalR subscriptions
  // ---------------------------------------------------------------------------

  private _subscribeToSignalR(): void {
    // ADR status updates (converting → identifying → extracting)
    this._subscriptions.push(
      this._signalRService.docOcrAdrStatusUpdate$.subscribe((event: DocOcrAdrStatusUpdatePayload) => {
        const item = this.fileItems.find(f => f.docFileId === event.docFileId);
        if (!item || item.status === 'matched' || item.status === 'needs-selection' || item.status === 'split' || item.status === 'confirmed' || item.status === 'error') {
          return;
        }

        const normalized = (event.status || '').toLowerCase().trim();
        if (normalized === 'split') {
          this._handleSplitStatus(item);
        } else if (normalized === 'converting') {
          this._advanceToStage(item, 'converting');
        } else if (normalized === 'identifying' || normalized === 'sentforadr') {
          this._advanceToStage(item, 'identifying');
        } else if (normalized === 'extracting' || normalized === 'identified') {
          this._advanceToStage(item, 'extracting');
        }
        this._cdr.detectChanges();
      })
    );

    // ADR complete — advance to "matching" stage (waiting for AI)
    this._subscriptions.push(
      this._signalRService.docOcrAdrComplete$.subscribe((event: DocOcrAdrCompletePayload) => {
        const item = this.fileItems.find(f => f.docFileId === event.docFileId);
        if (!item || item.status === 'matched' || item.status === 'needs-selection' || item.status === 'confirmed' || item.status === 'error') {
          return;
        }
        this._advanceToStage(item, 'matching');
        this._cdr.detectChanges();
      })
    );

    // Bulk upload task match result from AI
    this._subscriptions.push(
      this._signalRService.bulkUploadTaskMatchResult$.subscribe((event: BulkUploadTaskMatchResultPayload) => {
        const item = this.fileItems.find(f => f.docFileId === event.docFileId);
        if (!item) {
          return;
        }

        // Allow overriding 'needs-selection' when a valid match arrives —
        // polling may have prematurely set the file to 'needs-selection'
        // before the AI match result was ready.
        if (item.status === 'matched' || item.status === 'confirmed' || item.status === 'error') {
          return;
        }

        item.matchedTaskId = event.matchedTaskId;
        item.matchedTaskDescription = event.matchedTaskDescription;
        item.confidence = event.confidence;
        item.requiresManualSelection = event.requiresManualSelection;
        item.extractedDocumentTypeName = event.extractedDocumentTypeName;

        // Complete the matching stage
        this._completeAllStages(item);

        if (event.requiresManualSelection) {
          item.status = 'needs-selection';
        } else {
          item.status = 'matched';
          item.selectedTaskId = undefined;
          this._scheduleAutoConfirm(item);
        }
        this._cdr.detectChanges();
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Private — stage management
  // ---------------------------------------------------------------------------

  private _advanceToStage(item: BulkUploadFileItem, stageKey: string): void {
    let found = false;
    for (const stage of item.processingStages) {
      if (stage.key === stageKey) {
        stage.active = true;
        stage.complete = false;
        found = true;
        item.status = stageKey as BulkUploadFileStatus;
      } else if (!found) {
        // Previous stages are complete
        stage.complete = true;
        stage.active = false;
      } else {
        // Future stages remain pending
        stage.active = false;
        stage.complete = false;
      }
    }
  }

  private _handleSplitStatus(item: BulkUploadFileItem): void {
    this._completeAllStages(item);
    item.status = 'split';
    this._loadPendingBulkUploads();
  }

  private _completeAllStages(item: BulkUploadFileItem): void {
    for (const stage of item.processingStages) {
      stage.complete = true;
      stage.active = false;
    }
  }

  private _clearStages(item: BulkUploadFileItem): void {
    for (const stage of item.processingStages) {
      stage.complete = false;
      stage.active = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Private — load tasks for manual selection dropdown
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Private — polling fallback (mirrors AdminV2 AdrProcessingDialogComponent)
  // ---------------------------------------------------------------------------

  /**
   * Start polling as a safety net whenever there are files still processing.
   * Even when SignalR is connected, polling ensures files are not left stuck
   * if real-time events are missed (e.g. during transport upgrade cycles).
   */
  private _startPollingIfNeeded(): void {
    if (this._pollingActive) {
      return;
    }
    if (!this._hasProcessingFiles()) {
      return;
    }

    this._pollingActive = true;

    this._ngZone.runOutsideAngular(() => {
      this._pollingSubscription = interval(POLL_INTERVAL_MS).pipe(
        takeWhile(() => this._pollingActive)
      ).subscribe(() => {
        this._pollAllProcessingFiles();
      });
    });
  }

  private _stopPolling(): void {
    this._pollingActive = false;
    this._pollingSubscription?.unsubscribe();
    this._pollingSubscription = undefined;
  }

  private _hasProcessingFiles(): boolean {
    return this.fileItems.some(f =>
      f.status !== 'matched' &&
      f.status !== 'needs-selection' &&
      f.status !== 'split' &&
      f.status !== 'confirmed' &&
      f.status !== 'error'
    );
  }

  private _pollAllProcessingFiles(): void {
    const processingFiles = this.fileItems.filter(
      f => f.docFileId &&
        f.status !== 'matched' &&
        f.status !== 'needs-selection' &&
        f.status !== 'split' &&
        f.status !== 'confirmed' &&
        f.status !== 'error'
    );
    if (processingFiles.length === 0) {
      this._stopPolling();
      return;
    }
    for (const fileItem of processingFiles) {
      this._pollSingleFileStatus(fileItem);
    }
  }

  private _pollSingleFileStatus(fileItem: BulkUploadFileItem): void {
    if (!fileItem.docFileId || this._pollingInFlight.has(fileItem.id)) {
      return;
    }

    this._pollingInFlight.add(fileItem.id);

    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
    const url = `${docApiBase}/api/portal/bulk-upload/${this.applicationId}/status/${fileItem.docFileId}`;

    this._http.get<{
      docFileId: number;
      status: string;
      adrProcessed: boolean;
      matchResult?: {
        matchedTaskId: number | null;
        matchedTaskDescription: string | null;
        confidence: number;
        requiresManualSelection: boolean;
        extractedDocumentTypeName: string | null;
      };
    }>(url).subscribe({
      next: (result) => {
        this._pollingInFlight.delete(fileItem.id);
        this._ngZone.run(() => {
          this._deriveStageFromPollResult(fileItem, result);
        });
      },
      error: () => {
        this._pollingInFlight.delete(fileItem.id);
        // Polling error is non-critical; will retry next interval
      }
    });
  }

  private _deriveStageFromPollResult(fileItem: BulkUploadFileItem, result: any): void {
    if (fileItem.status === 'matched' || fileItem.status === 'needs-selection' ||
        fileItem.status === 'split' || fileItem.status === 'confirmed' || fileItem.status === 'error') {
      return;
    }

    const pollStatus = (result.status || '').toLowerCase().trim();

    // Auto-split parents are trashed server-side — mark terminal and let
    // _loadPendingBulkUploads restore the split children into the list.
    if (pollStatus === 'split') {
      this._handleSplitStatus(fileItem);
      this._cdr.detectChanges();
      return;
    }

    // If match result is available, apply it directly
    if (result.matchResult) {
      const mr = result.matchResult;
      fileItem.matchedTaskId = mr.matchedTaskId;
      fileItem.matchedTaskDescription = mr.matchedTaskDescription;
      fileItem.confidence = mr.confidence;
      fileItem.requiresManualSelection = mr.requiresManualSelection;
      fileItem.extractedDocumentTypeName = mr.extractedDocumentTypeName;
      this._completeAllStages(fileItem);
      fileItem.status = mr.requiresManualSelection ? 'needs-selection' : 'matched';
      if (!mr.requiresManualSelection) {
        this._scheduleAutoConfirm(fileItem);
      }
      this._cdr.detectChanges();
      return;
    }

    const normalized = pollStatus;

    // If the backend says "matching", the AI task-matcher is still working.
    // Advance to the matching stage and let polling continue.
    if (normalized === 'matching') {
      if (fileItem.status !== 'matching') {
        this._advanceToStage(fileItem, 'matching');
        this._cdr.detectChanges();
      }
      return;
    }

    // ADR is done and the backend is NOT in "matching" state, yet there is
    // no match result — the matcher errored or was skipped.  Fall back to
    // manual selection so the borrower can still assign a task.
    if (result.adrProcessed && !result.matchResult) {
      fileItem.requiresManualSelection = true;
      this._completeAllStages(fileItem);
      fileItem.status = 'needs-selection';
      this._cdr.detectChanges();
      return;
    }
    if (normalized === 'extracting' || normalized === 'identified') {
      if (fileItem.status === 'converting' || fileItem.status === 'identifying') {
        this._advanceToStage(fileItem, 'extracting');
        this._cdr.detectChanges();
      }
    } else if (normalized === 'identifying' || normalized === 'sentforadr') {
      if (fileItem.status === 'converting') {
        this._advanceToStage(fileItem, 'identifying');
        this._cdr.detectChanges();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Private — load tasks for manual selection dropdown
  // ---------------------------------------------------------------------------

  private _loadPendingBulkUploads(): void {
    if (!this.applicationId) {
      return;
    }

    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
    const url = `${docApiBase}/api/portal/bulk-upload/${this.applicationId}/pending`;

    this._http.get<{ pendingFiles: {
      docFileId: number; fileGuid: string; fileName: string; mimeType: string;
      borrowerNote: string; bulkUploadBatchId: string; adrProcessed: boolean;
      matchResult?: {
        matchedTaskId: number | null; matchedTaskDescription: string | null;
        confidence: number; requiresManualSelection: boolean;
        extractedDocumentTypeName: string | null;
      };
    }[] }>(url)
      .subscribe({
        next: (response) => {
          const knownDocFileIds = new Set(
            this.fileItems.map(f => f.docFileId).filter((id): id is number => id !== undefined)
          );
          const pending = (response.pendingFiles || []).filter(pf => !knownDocFileIds.has(pf.docFileId));
          if (pending.length === 0) {
            return;
          }

          const restoredItems: BulkUploadFileItem[] = pending.map(pf => {
            const mr = pf.matchResult;
            const hasMatch = !!mr;
            const isAutoMatched = hasMatch && !mr.requiresManualSelection;

            return {
              id: this._nextId++,
              file: new File([], pf.fileName, { type: pf.mimeType }),
              status: (hasMatch
                ? (isAutoMatched ? 'matched' : 'needs-selection')
                : (pf.adrProcessed ? 'needs-selection' : 'converting')) as BulkUploadFileStatus,
              docFileId: pf.docFileId,
              fileGuid: pf.fileGuid,
              matchedTaskId: mr?.matchedTaskId ?? undefined,
              matchedTaskDescription: mr?.matchedTaskDescription ?? undefined,
              confidence: mr?.confidence ?? undefined,
              requiresManualSelection: mr?.requiresManualSelection ?? true,
              extractedDocumentTypeName: mr?.extractedDocumentTypeName ?? undefined,
              borrowerNote: pf.borrowerNote || undefined,
              processingStages: hasMatch || pf.adrProcessed
                ? this._createCompletedStages()
                : this._createInitialStages()
            };
          });

          this.fileItems.push(...restoredItems);
          this.showProcessingPanel = true;
          this._startPollingIfNeeded();

          // Auto-confirm any restored files that were already matched
          for (const ri of restoredItems) {
            if (ri.status === 'matched') {
              this._scheduleAutoConfirm(ri);
            }
          }

          this._cdr.detectChanges();
        },
        error: () => {
          // Non-critical: pending files simply won't be restored
        }
      });
  }

  // ---------------------------------------------------------------------------
  // Private — auto-confirm matched files
  // ---------------------------------------------------------------------------

  /**
   * Schedule automatic confirmation for a file that the AI matched with high
   * confidence.  After a short delay the file is confirmed server-side,
   * animated out of the list, and the parent task view is refreshed.
   */
  private _scheduleAutoConfirm(item: BulkUploadFileItem): void {
    if (this._autoConfirmTimers.has(item.id) || this._autoConfirmInFlight.has(item.id)) {
      return;
    }

    const timer = setTimeout(() => {
      this._autoConfirmTimers.delete(item.id);
      if (this._destroyed || item.status !== 'matched') {
        return;
      }
      this._autoConfirmSingleFile(item);
    }, AUTO_CONFIRM_DELAY_MS);

    this._autoConfirmTimers.set(item.id, timer);
  }

  /**
   * Confirm a single auto-matched file: call the backend, slide the item out,
   * remove it from the list, and tell the parent to refresh the task view.
   */
  private _autoConfirmSingleFile(item: BulkUploadFileItem): void {
    if (!item.docFileId || !item.matchedTaskId) {
      return;
    }

    this._autoConfirmInFlight.add(item.id);

    const docApiBase = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
    const url = `${docApiBase}/api/portal/bulk-upload/confirm/${this.applicationId}`;

    const assignments = [{
      docFileId: item.docFileId,
      taskId: item.matchedTaskId,
      borrowerNote: null
    }];

    this._http.post<{ confirmResults: any[] }>(url, {
      applicationId: this.applicationId,
      assignments
    }).subscribe({
      next: () => {
        this._autoConfirmInFlight.delete(item.id);
        if (this._destroyed) {
          return;
        }

        item.status = 'confirmed';
        item.isSliding = true;
        this._cdr.detectChanges();

        // After the slide-out animation finishes, remove the item and refresh tasks
        setTimeout(() => {
          if (this._destroyed) {
            return;
          }

          if (item.thumbnailUrl && item.thumbnailUrl.startsWith('blob:')) {
            URL.revokeObjectURL(item.thumbnailUrl);
          }
          this.fileItems = this.fileItems.filter(f => f.id !== item.id);

          if (this.fileItems.length === 0) {
            this.showProcessingPanel = false;
          }

          // Notify parent to refresh the task list
          this.confirmed.emit();
          this._cdr.detectChanges();
        }, 500);
      },
      error: () => {
        this._autoConfirmInFlight.delete(item.id);
        // If auto-confirm fails, leave the file as 'matched' so the user
        // can still confirm it manually via the "Confirm & Submit All" button.
      }
    });
  }

  private _createCompletedStages(): ProcessingStage[] {
    return [
      { key: 'uploading', label: 'Uploading', complete: true, active: false },
      { key: 'converting', label: 'Converting', complete: true, active: false },
      { key: 'identifying', label: 'Identifying', complete: true, active: false },
      { key: 'extracting', label: 'Extracting', complete: true, active: false },
      { key: 'matching', label: 'Matching', complete: true, active: false }
    ];
  }

  private _loadAvailableTasks(): void {
    if (!this.applicationId) {
      return;
    }

    this._portalService.getTasksForAppId(this.applicationId).subscribe({
      next: (tasks: ApplicationTasksModel) => {
        // Combine requested + submitted tasks for the dropdown
        const allTasks = [
          ...(tasks.requested || []),
          ...(tasks.submitted || [])
        ];
        // Filter to RequestDocument tasks that allow upload
        this.availableTasks = allTasks.filter(
          t => t.borrowerTaskType === BorrowerTaskType.RequestDocument
        );
        this._cdr.detectChanges();
      },
      error: () => {
        // Tasks will just not be available for manual selection
      }
    });
  }
}
